/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { css, jsx, cssMap, keyframes, cx } from '@compiled/react';
import type { IntlShape } from 'react-intl';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { syncBlockMessages as messages } from '@atlaskit/editor-common/messages';
import { SYNCED_BLOCKS_DOCUMENTATION_URL } from '@atlaskit/editor-common/sync-block';
import type {
	ExtractInjectionAPI,
	FloatingToolbarCustomRenderContext,
} from '@atlaskit/editor-common/types';
import { FloatingToolbarButton as Button } from '@atlaskit/editor-common/ui';
import { ArrowKeyNavigationType, DropdownContainer } from '@atlaskit/editor-common/ui-menu';
import { getPageIdAndTypeFromConfluencePageAri } from '@atlaskit/editor-synced-block-provider';
import type {
	SyncBlockSourceInfo,
	SyncBlockStoreManager,
	ReferencesSourceInfo,
	SyncBlockProduct,
} from '@atlaskit/editor-synced-block-provider';
import type { SyncBlockJiraIssueType } from '@atlaskit/editor-synced-block-provider/types';
import { IconTile } from '@atlaskit/icon';
// eslint-disable-next-line import/order -- CI requires icon-lab imports before core icon imports.
import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';
import BugIcon from '@atlaskit/icon/core/bug';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import EpicIcon from '@atlaskit/icon/core/epic';
import PageIcon from '@atlaskit/icon/core/page';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StoryIcon from '@atlaskit/icon/core/story';
import SubtaskIcon from '@atlaskit/icon/core/subtasks';
import TaskIcon from '@atlaskit/icon/core/task';
import { ConfluenceIcon, JiraIcon, AtlassianIcon } from '@atlaskit/logo';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Text, Inline, Anchor, Stack } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { SyncedBlockPlugin } from '../syncedBlockPluginType';
import { SYNCED_BLOCK_BUTTON_TEST_ID } from '../types';

import { SyncedLocationsEmptyStateIllustration } from './assets/SyncedLocationsEmptyStateIllustration';

interface Props {
	api?: ExtractInjectionAPI<SyncedBlockPlugin>;
	floatingToolbarRenderContext?: FloatingToolbarCustomRenderContext;
	intl: IntlShape;
	isSource: boolean;
	localId: string;
	resourceId: string;
	syncBlockStore: SyncBlockStoreManager;
}

const fadeIn = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

const headingStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-ds--menu--heading-item]': {
		color: token('color.text.subtlest'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		marginBlock: `${token('space.050')} !important`,
	},
});

const dropdownItemStyles = css({
	// Reduce gap between icon and title
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'a > span': {
		columnGap: `${token('space.075')}`,
	},
});

// logo icon does not fit in ADS IconTile, hence we need custom styles to match with other icons
const logoTileStyles = css({
	backgroundColor: token('color.background.neutral'),
	width: '20px',
	height: '20px',
	borderRadius: token('radius.tile'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const SYNCED_LOCATIONS_DROPDOWN_TEST_ID = 'synced-block-synced-locations-dropdown';

const styles = cssMap({
	title: {
		color: token('color.text.subtle'),
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
	},
	note: {
		color: token('color.text.subtlest'),
		whiteSpace: 'nowrap',
	},
	lozenge: {
		marginInlineStart: token('space.075'),
		minWidth: '60px',
	},
	noResultsContainer: {
		width: '235px',
		textAlign: 'center',
	},
	activationDropdownContent: {
		width: '400px',
		paddingBlock: token('space.0'),
	},
	activationNoResultsContainer: {
		boxSizing: 'border-box',
		width: '400px',
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
	},
	activationNoResultsContent: {
		width: '290px',
		marginInline: 'auto',
		textAlign: 'center',
	},
	dropdownContent: {
		width: '342px',
		maxHeight: '304px',
		paddingBlock: token('space.025'),
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	containerWithMinHeight: {
		minHeight: '144px',
	},
	contentContainer: {
		width: '100%',
		alignSelf: 'stretch',
		overflowY: 'auto',
		animation: `${fadeIn} 700ms ease-in-out`,
	},
	errorContainer: {
		width: '235px',
		display: 'flex',
	},
	errorIcon: {
		marginBlock: token('space.negative.050'),
	},
	learnMoreLink: {
		textDecoration: 'none',
	},
	requestAccess: {
		width: '106px',
		whiteSpace: 'nowrap',
		marginInlineStart: token('space.075'),
		color: token('color.text.subtlest'),
	},
});

type FetchStatus = 'none' | 'loading' | 'success' | 'error';

interface ReferenceDataState {
	fetchStatus: FetchStatus;
	referenceData: SyncBlockSourceInfo[];
}

const shouldApplyMinHeight = (fetchStatus: FetchStatus, itemCount: number) => {
	// When there are 1/2 items, dropdown height is less than minHeight 144px
	return !(fetchStatus === 'success' && itemCount > 0);
};

const ItemTitle = ({
	title,
	formatMessage,
	onSameDocument,
	isSource,
	hasAccess,
	productType,
}: {
	formatMessage: IntlShape['formatMessage'];
	hasAccess?: boolean;
	isSource?: boolean;
	onSameDocument?: boolean;
	productType?: SyncBlockProduct;
	title: string;
}) => {
	return (
		<Inline>
			<Box as="span" xcss={styles.title}>
				{title}
			</Box>
			{onSameDocument && (
				<Box as="span" xcss={styles.note}>
					&nbsp;-{' '}
					{formatMessage(
						productType === 'confluence-page'
							? messages.syncedLocationDropdownTitleNoteForConfluencePage
							: messages.syncedLocationDropdownTitleNoteForJiraWorkItem,
					)}
				</Box>
			)}
			{isSource && (
				<Box as="span" xcss={styles.lozenge}>
					<Lozenge>{formatMessage(messages.syncedLocationDropdownSourceLozenge)}</Lozenge>
				</Box>
			)}
			{!hasAccess && (
				<Box as="span" xcss={styles.requestAccess}>
					{formatMessage(messages.syncedLocationDropdownRequestAccess)}
				</Box>
			)}
		</Inline>
	);
};

const productIconMap = {
	'confluence-page': ConfluenceIcon,
	'jira-work-item': JiraIcon,
};

const subTypeIconMap = {
	live: PageLiveDocIcon,
	page: PageIcon,
};

const getConfluenceSubTypeIcon = (sourceAri: string, subType?: string | null) => {
	try {
		const { type: pageType } = getPageIdAndTypeFromConfluencePageAri({ ari: sourceAri });
		if (pageType === 'blogpost') {
			return QuotationMarkIcon;
		} else {
			return subType && subType in subTypeIconMap
				? subTypeIconMap[subType as keyof typeof subTypeIconMap]
				: PageIcon;
		}
	} catch {
		return PageIcon;
	}
};

const ProductIcon = ({ product }: { product?: SyncBlockProduct }) => {
	const ProductIcon = product ? (productIconMap[product] ?? AtlassianIcon) : AtlassianIcon;

	return (
		<span css={logoTileStyles}>
			<ProductIcon size="xxsmall" appearance="neutral" />
		</span>
	);
};

// Map AGG issue-type names to ADS icons. The mapping is by the English `name` returned
// from AGG because Jira's REST/GraphQL API does not localise it at this layer. Custom
// (non-default) issue types fall through to the AGG `iconUrl`.
//
// Type the icons as the same shape as `TaskIcon` so we don't import `NewCoreIconProps`
// from a private icon entrypoint.
type IssueTypeIconComponent = typeof TaskIcon;
const jiraIssueTypeIconMap: Record<
	string,
	{ icon: IssueTypeIconComponent; messageKey: keyof typeof messages }
> = {
	Task: { icon: TaskIcon, messageKey: 'syncedLocationDropdownIssueTypeTask' },
	Bug: { icon: BugIcon, messageKey: 'syncedLocationDropdownIssueTypeBug' },
	Story: { icon: StoryIcon, messageKey: 'syncedLocationDropdownIssueTypeStory' },
	Epic: { icon: EpicIcon, messageKey: 'syncedLocationDropdownIssueTypeEpic' },
	Subtask: { icon: SubtaskIcon, messageKey: 'syncedLocationDropdownIssueTypeSubtask' },
	'Sub-task': { icon: SubtaskIcon, messageKey: 'syncedLocationDropdownIssueTypeSubtask' },
};

/**
 * Creates an icon component from a custom Jira issue-type `iconUrl` that conforms to the
 * ADS icon component contract expected by `IconTile`. This lets us reuse `IconTile` for
 * custom issue types — ensuring consistent sizing, background, and border-radius with the
 * standard ADS icons used for known issue types (Bug, Story, etc.).
 *
 * The returned component ignores ADS icon props (color, spacing, etc.) because the image
 * is an external raster/SVG asset that doesn't respond to design tokens.
 */
const customIconCache = new Map<string, IssueTypeIconComponent>();

const createCustomIssueTypeIcon = (iconUrl: string): IssueTypeIconComponent => {
	const cached = customIconCache.get(iconUrl);
	if (cached) {
		return cached;
	}
	const CustomIssueTypeIcon = () => <img src={iconUrl} alt="" width="12" height="12" />;
	CustomIssueTypeIcon.displayName = 'CustomIssueTypeIcon';
	customIconCache.set(iconUrl, CustomIssueTypeIcon);
	return CustomIssueTypeIcon;
};

/**
 * Returns the icon to render for a Jira issue type, or `null` when neither an ADS icon
 * mapping nor an AGG-provided `iconUrl` is available so the caller can fall back to a
 * generic product icon.
 *
 * Implemented as a plain function (not a React component) so the `null` check actually
 * narrows — a JSX expression always evaluates to a truthy `ReactElement` object,
 * meaning callers cannot distinguish a "would render nothing" component from one that
 * renders an icon.
 */
const renderJiraIssueTypeIcon = (
	issueType: SyncBlockJiraIssueType,
	intl: IntlShape,
): ReactNode | null => {
	const mapped = jiraIssueTypeIconMap[issueType.name];
	if (mapped) {
		const label = intl.formatMessage(messages[mapped.messageKey]);
		return <IconTile icon={mapped.icon} label={label} appearance={'gray'} size="xsmall" />;
	}

	// Custom Jira issue types — render inside `IconTile` using a wrapper component so the
	// icon gets the same tile background, border-radius, and sizing as known issue types.
	if (issueType.iconUrl) {
		const CustomIcon = createCustomIssueTypeIcon(issueType.iconUrl);
		const label = intl.formatMessage(messages.syncedLocationDropdownIssueTypeGeneric);
		return <IconTile icon={CustomIcon} label={label} appearance={'gray'} size="xsmall" />;
	}

	return null;
};

const ItemIcon = ({ reference, intl }: { intl: IntlShape; reference: SyncBlockSourceInfo }) => {
	const { hasAccess, subType, productType, sourceAri, issueType } = reference;

	if (productType === 'confluence-page' && hasAccess) {
		return (
			<IconTile
				icon={getConfluenceSubTypeIcon(sourceAri, subType)}
				label=""
				appearance={'gray'}
				size="xsmall"
			/>
		);
	}

	// Render a Jira issue-type icon when we have one, gated by the shared rollout flag.
	// Falls through to the generic product icon when:
	//   - the gate is off,
	//   - we don't have access (issueType is not surfaced for no-access references),
	//   - AGG returned no `issueType` (partial index, deleted, etc.), or
	//   - the issue type is unrecognised AND has no `iconUrl`.
	if (productType === 'jira-work-item' && hasAccess && issueType) {
		const icon = renderJiraIssueTypeIcon(issueType, intl);
		if (icon !== null) {
			return icon;
		}
	}

	// Generic product fallback for `jira-work-item` (and any future product).
	return <ProductIcon product={productType} />;
};

export const processReferenceData = (
	referenceData: ReferencesSourceInfo['references'],
	intl: IntlShape,
): SyncBlockSourceInfo[] => {
	const { formatMessage } = intl;
	const sourceInfoMap: SourceInfoMap = new Map();
	referenceData?.forEach((reference) => {
		if (!reference) {
			return;
		}
		if (sourceInfoMap.has(reference.sourceAri)) {
			sourceInfoMap.get(reference.sourceAri)?.push(reference);
		} else {
			sourceInfoMap.set(reference.sourceAri, [reference]);
		}
	});

	for (const references of sourceInfoMap.values()) {
		if (references.length > 1) {
			references.forEach(
				(reference, index) =>
					(reference.title = `${reference.title === '' && reference.hasAccess ? formatMessage(messages.syncedLocationDropdownUntitledPage) : reference.title}: ${formatMessage(
						messages.syncedLocationDropdownTitleBlockIndex,
						{ index: index + 1 },
					)}`),
			);
		}
	}

	const sortedReferences = Array.from(sourceInfoMap.values())
		.flat()
		.sort((a, b) => {
			if (a.isSource !== b.isSource) {
				return b.isSource ? 1 : -1;
			}

			if (a.hasAccess !== b.hasAccess) {
				return a.hasAccess ? -1 : 1;
			}

			return (a.title || '').localeCompare(b.title || '');
		});

	return sortedReferences;
};

export const SyncedLocationDropdown = ({
	syncBlockStore,
	resourceId,
	intl,
	isSource,
	localId,
	api,
	floatingToolbarRenderContext,
}: Props): JSX.Element => {
	if (!fg('platform_synced_block_patch_13')) {
		return (
			<LegacySyncedLocationDropdown
				syncBlockStore={syncBlockStore}
				resourceId={resourceId}
				intl={intl}
				isSource={isSource}
				localId={localId}
				api={api}
			/>
		);
	}

	return (
		<EditorPositionedSyncedLocationDropdown
			syncBlockStore={syncBlockStore}
			resourceId={resourceId}
			intl={intl}
			isSource={isSource}
			localId={localId}
			api={api}
			floatingToolbarRenderContext={floatingToolbarRenderContext}
		/>
	);
};

const EditorPositionedSyncedLocationDropdown = ({
	syncBlockStore,
	resourceId,
	intl,
	isSource,
	localId,
	api,
	floatingToolbarRenderContext,
}: Props): JSX.Element => {
	const triggerTitle = intl.formatMessage(messages.syncedLocationDropdownTitle);
	const [isOpen, setIsOpen] = useState(false);

	const content = isOpen ? (
		<DropdownContent
			syncBlockStore={syncBlockStore}
			resourceId={resourceId}
			intl={intl}
			isSource={isSource}
			localId={localId}
			api={api}
		/>
	) : null;

	const toggleOpen = useCallback(() => {
		setIsOpen((currentIsOpen) => !currentIsOpen);
	}, []);

	const closeDropdown = useCallback(() => {
		setIsOpen(false);
	}, []);

	const setDisableParentScroll = floatingToolbarRenderContext?.setDisableParentScroll;

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		setDisableParentScroll?.(true);

		return () => {
			setDisableParentScroll?.(false);
		};
	}, [isOpen, setDisableParentScroll]);

	const trigger = useMemo(
		() => (
			<Button
				areAnyNewToolbarFlagsEnabled={true}
				testId={SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarSyncedLocationsTrigger}
				selected={isOpen}
				iconAfter={
					<ChevronDownIcon color="currentColor" spacing="spacious" label="" size="small" />
				}
				onClick={toggleOpen}
				ariaHasPopup
			>
				{triggerTitle}
			</Button>
		),
		[isOpen, toggleOpen, triggerTitle],
	);

	return (
		<DropdownContainer
			testId={SYNCED_LOCATIONS_DROPDOWN_TEST_ID}
			isOpen={isOpen}
			trigger={trigger}
			handleClickOutside={closeDropdown}
			handleEscapeKeydown={closeDropdown}
			mountTo={floatingToolbarRenderContext?.popupsMountPoint}
			boundariesElement={floatingToolbarRenderContext?.popupsBoundariesElement}
			scrollableElement={floatingToolbarRenderContext?.popupsScrollableElement}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			arrowKeyNavigationProviderOptions={{ type: ArrowKeyNavigationType.MENU }}
		>
			{content}
		</DropdownContainer>
	);
};

const LegacySyncedLocationDropdown = ({
	syncBlockStore,
	resourceId,
	intl,
	isSource,
	localId,
	api,
}: Props): JSX.Element => {
	const triggerTitle = intl.formatMessage(messages.syncedLocationDropdownTitle);
	const [isOpen, setIsOpen] = useState(false);

	const content = isOpen ? (
		<DropdownContent
			syncBlockStore={syncBlockStore}
			resourceId={resourceId}
			intl={intl}
			isSource={isSource}
			localId={localId}
			api={api}
		/>
	) : null;

	return (
		<DropdownMenu
			isOpen={isOpen}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onOpenChange={({ isOpen }) => setIsOpen(isOpen)}
			testId={SYNCED_LOCATIONS_DROPDOWN_TEST_ID}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			trigger={({ triggerRef, ...triggerProps }) => (
				<Button
					ref={triggerRef}
					areAnyNewToolbarFlagsEnabled={true}
					selected={isOpen}
					iconAfter={
						<ChevronDownIcon color="currentColor" spacing="spacious" label="" size="small" />
					}
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...triggerProps}
				>
					{triggerTitle}
				</Button>
			)}
		>
			{content}
		</DropdownMenu>
	);
};

export const SyncedLocationDropdownWithCount = ({
	syncBlockStore,
	resourceId,
	intl,
	isSource,
	localId,
	api,
	floatingToolbarRenderContext,
}: Props): JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);
	const { fetchStatus, referenceData } = useReferenceData({
		intl,
		isSource,
		localId,
		resourceId,
		syncBlockStore,
	});
	const referenceCount = useMemo(
		() => referenceData.filter(({ isSource: isSourceItem }) => !isSourceItem).length,
		[referenceData],
	);
	const tooltipContent =
		!isOpen && fetchStatus === 'success' && referenceCount === 0
			? intl.formatMessage(messages.syncedLocationDropdownNoReferencesTooltip)
			: null;
	const content = isOpen ? (
		<DropdownContentWithReferenceData
			resourceId={resourceId}
			intl={intl}
			api={api}
			fetchStatus={fetchStatus}
			referenceData={referenceData}
		/>
	) : null;

	const toggleOpen = useCallback(() => {
		setIsOpen((currentIsOpen) => !currentIsOpen);
	}, []);

	const closeDropdown = useCallback(() => {
		setIsOpen(false);
	}, []);

	const setDisableParentScroll = floatingToolbarRenderContext?.setDisableParentScroll;

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		setDisableParentScroll?.(true);

		return () => {
			setDisableParentScroll?.(false);
		};
	}, [isOpen, setDisableParentScroll]);

	const trigger = useMemo(
		() => (
			<Button
				areAnyNewToolbarFlagsEnabled={true}
				testId={SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarSyncedLocationsTrigger}
				selected={isOpen}
				iconAfter={
					<ChevronDownIcon color="currentColor" spacing="spacious" label="" size="small" />
				}
				onClick={toggleOpen}
				ariaHasPopup
				tooltipContent={tooltipContent ?? undefined}
			>
				<SyncedLocationTriggerContent
					intl={intl}
					fetchStatus={fetchStatus}
					referenceCount={referenceCount}
				/>
			</Button>
		),
		[fetchStatus, intl, isOpen, referenceCount, toggleOpen, tooltipContent],
	);

	return (
		<DropdownContainer
			alignX="center"
			testId={SYNCED_LOCATIONS_DROPDOWN_TEST_ID}
			isOpen={isOpen}
			trigger={trigger}
			handleClickOutside={closeDropdown}
			handleEscapeKeydown={closeDropdown}
			mountTo={floatingToolbarRenderContext?.popupsMountPoint}
			boundariesElement={floatingToolbarRenderContext?.popupsBoundariesElement}
			scrollableElement={floatingToolbarRenderContext?.popupsScrollableElement}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			arrowKeyNavigationProviderOptions={{ type: ArrowKeyNavigationType.MENU }}
		>
			{content}
		</DropdownContainer>
	);
};

type SourceInfoMap = Map<string, SyncBlockSourceInfo[]>;

const DropdownContent = ({ syncBlockStore, resourceId, intl, isSource, localId, api }: Props) => {
	const [fetchStatus, setFetchStatus] = useState<FetchStatus>('none');
	const [referenceData, setReferenceData] = useState<SyncBlockSourceInfo[]>([]);

	useEffect(() => {
		setFetchStatus('loading');

		const getReferenceData = async () => {
			const response = await syncBlockStore.fetchReferencesSourceInfo(
				resourceId,
				localId,
				isSource,
			);

			if (response.error) {
				setFetchStatus('error');
				return;
			}
			setReferenceData(processReferenceData(response.references, intl));
			setFetchStatus('success');
		};
		void getReferenceData();
	}, [syncBlockStore, intl, isSource, localId, resourceId]);

	return (
		<DropdownContentWithReferenceData
			resourceId={resourceId}
			intl={intl}
			api={api}
			fetchStatus={fetchStatus}
			referenceData={referenceData}
		/>
	);
};

const useReferenceData = ({
	syncBlockStore,
	resourceId,
	intl,
	isSource,
	localId,
}: Pick<
	Props,
	'syncBlockStore' | 'resourceId' | 'intl' | 'isSource' | 'localId'
>): ReferenceDataState => {
	const [isNewSourceBlock] = useState(
		() => isSource && syncBlockStore.sourceManager.isNewSourceBlock(resourceId),
	);
	const [state, setState] = useState<{
		fetchStatus: FetchStatus;
		referenceData: SyncBlockSourceInfo[];
	}>(() => ({
		fetchStatus: isNewSourceBlock ? 'success' : 'loading',
		referenceData: [],
	}));

	useEffect(() => {
		let isCurrentRequest = true;
		setState({
			fetchStatus: isNewSourceBlock ? 'success' : 'loading',
			referenceData: [],
		});

		const getReferenceData = async () => {
			const response = await syncBlockStore.fetchReferencesSourceInfo(
				resourceId,
				localId,
				isSource,
			);

			if (!isCurrentRequest) {
				return;
			}

			if (isNewSourceBlock) {
				syncBlockStore.sourceManager.clearNewSourceBlock(resourceId);
			}
			if (response.error) {
				setState({ fetchStatus: 'error', referenceData: [] });
				return;
			}
			setState({
				fetchStatus: 'success',
				referenceData: processReferenceData(response.references, intl),
			});
		};

		void getReferenceData();

		return () => {
			isCurrentRequest = false;
		};
	}, [syncBlockStore, resourceId, intl, isSource, localId, isNewSourceBlock]);

	return {
		fetchStatus: state.fetchStatus,
		referenceData: state.referenceData,
	};
};

const SyncedLocationTriggerContent = ({
	intl,
	fetchStatus,
	referenceCount,
}: Pick<ReferenceDataState, 'fetchStatus'> & { intl: IntlShape; referenceCount: number }) => {
	const { formatMessage } = intl;
	const triggerTitle = formatMessage(messages.syncedLocationDropdownTitle);
	const syncedLocationCount = referenceCount === 0 ? 0 : referenceCount + 1;

	switch (fetchStatus) {
		case 'loading':
			return (
				<Inline alignBlock="center" space="space.050">
					{triggerTitle}
					<Spinner size="small" label={formatMessage(messages.syncedLocationDropdownLoading)} />
				</Inline>
			);
		case 'success': {
			const count = syncedLocationCount > 99 ? '99+' : intl.formatNumber(syncedLocationCount);

			return formatMessage(messages.syncedLocationDropdownTitleWithCount, { count });
		}
		case 'none':
		case 'error':
			return triggerTitle;
	}
};

type DropdownContentProps = Pick<Props, 'resourceId' | 'intl' | 'api'> &
	Pick<ReferenceDataState, 'fetchStatus' | 'referenceData'>;

const DropdownContentWithReferenceData = ({
	resourceId,
	intl,
	api,
	fetchStatus,
	referenceData,
}: DropdownContentProps) => {
	const { formatMessage } = intl;

	const handleLocationClick = () => {
		api?.analytics?.actions?.fireAnalyticsEvent({
			eventType: EVENT_TYPE.OPERATIONAL,
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.SYNCED_BLOCK,
			actionSubjectId: ACTION_SUBJECT_ID.SYNCED_BLOCK_CLICK_SYNCED_LOCATION,
			attributes: {
				resourceId,
			},
		});
	};

	const content = () => {
		switch (fetchStatus) {
			case 'loading':
				return <LoadingScreen formatMessage={formatMessage} />;
			case 'error':
				return <ErrorScreen formatMessage={formatMessage} />;
			case 'success':
				if (referenceData.length > 0) {
					return (
						<div
							css={[styles.contentContainer, headingStyles]}
							data-testid="synced-locations-dropdown-content"
						>
							<DropdownItemGroup
								title={formatMessage(messages.syncedLocationDropdownHeading, {
									count: `${referenceData.length > 99 ? '99+' : referenceData.length}`,
								})}
							>
								{referenceData.map((reference) => {
									const title =
										reference.title === '' && reference.hasAccess
											? formatMessage(messages.syncedLocationDropdownUntitledPage)
											: reference.title || reference.url || '';

									return (
										<div key={reference.title} css={dropdownItemStyles}>
											<Tooltip content={title}>
												<DropdownItem
													elemBefore={<ItemIcon reference={reference} intl={intl} />}
													href={reference.url}
													target="_blank"
													key={reference.title}
													rel="noopener noreferrer"
													// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
													onClick={() => handleLocationClick()}
												>
													<ItemTitle
														title={title}
														formatMessage={formatMessage}
														onSameDocument={reference.onSameDocument}
														isSource={reference.isSource}
														hasAccess={reference.hasAccess}
														productType={reference.productType}
													/>
												</DropdownItem>
											</Tooltip>
										</div>
									);
								})}
							</DropdownItemGroup>
						</div>
					);
				} else {
					return <NoResultScreen formatMessage={formatMessage} />;
				}
		}
	};

	return (
		<Box
			xcss={cx(
				styles.dropdownContent,
				expValEqualsNoExposure('platform_editor_sync_block_activation', 'isEnabled', true) &&
					fetchStatus === 'success' &&
					referenceData.length === 0 &&
					styles.activationDropdownContent,
				shouldApplyMinHeight(fetchStatus, referenceData.length) && styles.containerWithMinHeight,
			)}
		>
			{content()}
		</Box>
	);
};

const LoadingScreen = ({ formatMessage }: { formatMessage: IntlShape['formatMessage'] }) => {
	return (
		<Box>
			<Spinner label={formatMessage(messages.syncedLocationDropdownLoading)} />
		</Box>
	);
};

const ErrorScreen = ({ formatMessage }: { formatMessage: IntlShape['formatMessage'] }) => {
	return (
		<Box xcss={styles.errorContainer} testId="synced-locations-dropdown-content-error">
			<Box xcss={styles.errorIcon}>
				<StatusErrorIcon
					color={token('color.icon.danger')}
					spacing="spacious"
					label=""
					size="small"
				/>
			</Box>
			<Text as="p" size="medium">
				{formatMessage(messages.syncedLocationDropdownError)}
			</Text>
		</Box>
	);
};

const NoResultScreen = ({ formatMessage }: { formatMessage: IntlShape['formatMessage'] }) => {
	return expValEquals('platform_editor_sync_block_activation', 'isEnabled', true) ? (
		<Box
			xcss={styles.activationNoResultsContainer}
			testId="synced-locations-dropdown-content-no-results"
		>
			<Stack alignInline="center" xcss={styles.activationNoResultsContent} space="space.150">
				<SyncedLocationsEmptyStateIllustration />
				<Text as="p">{formatMessage(messages.syncedLocationDropdownActivationNoResults)}</Text>
				<Text as="p">
					<Anchor
						href={SYNCED_BLOCKS_DOCUMENTATION_URL}
						target="_blank"
						rel="noopener noreferrer"
						xcss={styles.learnMoreLink}
					>
						{formatMessage(messages.syncedLocationDropdownLearnMoreLink)}
					</Anchor>
				</Text>
			</Stack>
		</Box>
	) : (
		<Stack
			xcss={styles.noResultsContainer}
			space="space.100"
			testId="synced-locations-dropdown-content-no-results"
		>
			<Text as="p">{formatMessage(messages.syncedLocationDropdownNoResults)}</Text>
			<Text as="p">
				<Anchor
					href={SYNCED_BLOCKS_DOCUMENTATION_URL}
					target="_blank"
					rel="noopener noreferrer"
					xcss={styles.learnMoreLink}
				>
					{formatMessage(messages.syncedLocationDropdownLearnMoreLink)}
				</Anchor>
			</Text>
		</Stack>
	);
};
